import { CalendarPlusIcon, ChevronLeft, GripVerticalIcon, MapPinIcon, SparklesIcon } from 'assets/icons';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PillTabs from 'components/tabs/PillTabs';
import { EVENT_CATEGORIES, eventCategoryOf, eventCategoryText } from 'constants/eventCategories';
import { sectorOf } from 'helper/geo';
import useText from 'hooks/useText';
import { deadlineOf, proposeTour, routeKm, slotLabel } from 'pages/planning/planningItems';

const FILTERS = ['all', 'prospection', 'rdv_client', 'visite_client', 'embauche'];

/**
 * « À placer », beside the planning: the visits booked while prospecting,
 * grouped by sector to save road, each to drag into the week before its
 * deadline. Each sector proposes its tour: the first slot where its visits
 * fit, in the order of the road.
 */
const PlacePanel = ({ visits, events, filter, onFilter, onBack, onDragStart, onDragEnd, onPlaceTour, draggingId }) => {
  const { text, language } = useText('planning');
  const now = new Date();
  const counts = Object.fromEntries(FILTERS.map((id) => [id, id === 'all' ? visits.length : visits.filter((visit) => visit.category === id).length]));
  const shown = visits.filter((visit) => filter === 'all' || visit.category === filter);
  const groups = {};
  shown.forEach((visit) => {
    const sector = sectorOf(visit.company?.city);
    groups[sector] = [...(groups[sector] ?? []), visit];
  });
  const sorted = Object.entries(groups).sort((a, b) => b[1].length - a[1].length || a[1][0].dueBy.localeCompare(b[1][0].dueBy));

  return (
    <aside className="flex w-[343px] shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white">
      <div className="flex flex-col gap-3 p-4 pb-2">
        <div className="flex items-center gap-3">
          <button type="button" aria-label={text('backToWeek')} onClick={onBack} className="rounded-lg p-1.5 hover:bg-gray-100">
            <ChevronLeft wh={20} color="currentColor" />
          </button>
          <p className="ft-b font-semibold text-gray-900">{text('toPlace')}</p>
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{visits.length}</span>
        </div>
        <p className="text-xs leading-4 text-gray-600">{text('toPlaceHint')}</p>
        <div>
          <PillTabs
            wrap
            activeTab={filter}
            onChange={onFilter}
            tabs={FILTERS.map((id) => ({
              id,
              // « Toutes » carries its count in its label, the others in a badge.
              label: id === 'all' ? text('allVisits', { count: counts.all }) : eventCategoryText(language, id, 'label'),
              count: id === 'all' ? undefined : counts[id],
              disabled: id !== 'all' && !counts[id],
            }))}
          />
        </div>
      </div>
      <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {!shown.length && (
          <CompactEmptyState compact glyph="squareCheck" title={text('noVisitTitle')} description={text('noVisitText')} />
        )}
        {sorted.map(([sector, list]) => {
          const tour = proposeTour(list.filter((visit) => visit.company), events, now);
          const km = Math.round(routeKm(list));
          const located = list.filter((visit) => visit.company).length;
          return (
            <div key={sector} className="mt-2 flex flex-col gap-2">
              {/* The sector stays at the top while its visits scroll. */}
              <div className="sticky top-0 z-10 -mx-4 flex items-center justify-between bg-white px-4 py-2">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                  <MapPinIcon wh={16} color="currentColor" />
                  {sector}
                </p>
                <p className="text-xs text-gray-400">
                  {text(list.length > 1 ? 'visitsMany' : 'visitsOne', { count: list.length })} ·{' '}
                  {list.length > 1 ? text('km', { km: Math.max(1, km) }) : text('alone')}
                </p>
              </div>
              {tour && (
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{slotLabel(tour.start, tour.end, text)}</p>
                    <p className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                      <span className="flex text-gray-500">
                        <SparklesIcon wh={14} color="currentColor" />
                      </span>
                      {tour.ordered.length < located
                        ? text(tour.ordered.length === 1 ? 'mostUrgentOne' : 'mostUrgentMany', { count: tour.ordered.length })
                        : text('tourProposed')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onPlaceTour(tour, sector)}
                    className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    <CalendarPlusIcon wh={18} color="currentColor" />
                    {text('place')}
                  </button>
                </div>
              )}
              {list.map((visit) => {
                const category = EVENT_CATEGORIES[eventCategoryOf(visit.category)];
                const deadline = deadlineOf(visit, now, text);
                return (
                  <div
                    key={visit.id}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', visit.id);
                      event.dataTransfer.effectAllowed = 'move';
                      onDragStart(visit);
                    }}
                    onDragEnd={onDragEnd}
                    className={`flex cursor-grab items-center gap-2 rounded-xl border p-3 active:cursor-grabbing ${
                      draggingId === visit.id ? 'border-dashed border-gray-200 bg-gray-100 opacity-70' : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    <span className="flex shrink-0 text-gray-400">
                      <GripVerticalIcon wh={16} color="currentColor" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{visit.company?.name ?? visit.reason}</p>
                      <p className="mt-1 flex items-center gap-2 text-xs">
                        <span className={`rounded px-1.5 py-0.5 font-medium ${category.tag}`}>{eventCategoryText(language, visit.category, 'label')}</span>
                        <span className={deadline.urgent ? 'text-red-700' : 'text-gray-600'}>{deadline.text}</span>
                      </p>
                      {visit.company && visit.reason && <p className="mt-1 truncate text-xs text-gray-600">{visit.reason}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default PlacePanel;
